'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { Mail, Zap, Clock, TrendingUp, BarChart2, CheckCircle, AlertCircle } from 'lucide-react';
import { subscribeNewsletter } from '@/lib/api-client';
import { useAuth } from '@/context/AuthContext';

const HIGHLIGHTS = [
  { icon: Clock,      label: 'Weekly Digest',   desc: 'Delivered every Monday — no spam, no noise.' },
  { icon: Zap,        label: 'Curated Stories',  desc: 'Only the market moves that actually matter.' },
  { icon: TrendingUp, label: 'Market Movers',    desc: 'Top gainers, losers, and sector rotation.' },
  { icon: BarChart2,  label: 'IPO Tracker',      desc: 'Upcoming listings and post-IPO performance.' },
];

function UnsubscribedBanner({ reason }: { reason: string }) {
  return (
    <div className="mb-6 flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl px-5 py-4 text-sm">
      <AlertCircle size={16} className="flex-shrink-0" />
      {reason === 'already'
        ? 'You were already unsubscribed from Market Bullets.'
        : 'You have been successfully unsubscribed from Market Bullets.'}
    </div>
  );
}

function NewsletterForm() {
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const unsubscribed = searchParams.get('unsubscribed');

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    try {
      const res = await subscribeNewsletter(email.trim(), user?.id);
      setMessage(res.message);
      setStatus('success');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div>
      {unsubscribed && <UnsubscribedBanner reason={unsubscribed} />}

      {status === 'success' ? (
        <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl px-5 py-4 text-sm">
          <CheckCircle size={16} className="flex-shrink-0" />
          {message}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            required
            className="flex-1 bg-card-bg border border-default focus:border-sky-500 rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-6 py-3 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white font-semibold text-sm rounded-xl transition-colors whitespace-nowrap"
          >
            {status === 'loading' ? 'Subscribing…' : 'Subscribe — It\'s Free'}
          </button>
        </form>
      )}

      {status === 'error' && (
        <p className="mt-2 text-sm text-red-400">{message}</p>
      )}

      <p className="mt-3 text-xs text-slate-600">
        No spam. Unsubscribe at any time. We respect your inbox.
      </p>
    </div>
  );
}

export default function NewsletterPage() {
  return (
    <div className="min-h-screen bg-surface">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-500/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-2 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-6">
            <Mail size={12} />
            FREE WEEKLY NEWSLETTER
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 leading-tight tracking-tight">
            Market Bullets
          </h1>

          <p className="text-xl text-sky-400 font-semibold mb-3">
            The newsletter that respects your time.
          </p>

          <p className="text-fg-muted text-base leading-relaxed mb-10 max-w-xl mx-auto">
            Every week, we distill the noise into a clean digest of the market moves, stories, and data
            points that self-directed investors actually care about — in under 5 minutes.
          </p>

          <div className="max-w-xl mx-auto">
            <Suspense>
              <NewsletterForm />
            </Suspense>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="max-w-4xl mx-auto px-2 py-12">
        <h2 className="text-center text-xl font-bold text-white mb-8">What's inside every issue</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-start gap-4 bg-card-bg border border-default rounded-xl p-5">
              <div className="w-9 h-9 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                <Icon size={17} className="text-sky-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white mb-0.5">{label}</div>
                <div className="text-xs text-fg-muted leading-relaxed">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Promise section */}
      <section className="max-w-3xl mx-auto px-2 py-12 text-center">
        <div className="bg-card-bg border border-default rounded-2xl p-8">
          <h2 className="text-lg font-bold text-white mb-3">Our promise to you</h2>
          <p className="text-fg-muted text-sm leading-relaxed mb-6 max-w-lg mx-auto">
            No sponsored content hidden in plain sight. No daily barrages. No clickbait. Just a
            concise, honest look at what moved markets — every Monday morning before the bell.
          </p>
          <ul className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-8 text-sm">
            {['One email per week', 'No ads, ever', 'Unsubscribe instantly'].map((item) => (
              <li key={item} className="flex items-center justify-center gap-2 text-emerald-400">
                <CheckCircle size={14} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
